/**
 * 游戏管理页面 - 设备列表
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDevices } from '../services/api';
import { getUserDeviceIDs } from '../services/auth';
import DeviceCard from '../components/DeviceCard';
import TabBar from '../components/TabBar';
import Loading from '../components/Loading';
import EmptyState from '../components/EmptyState';
import './GameManagement.css';

const GameManagement = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDevices(getUserDeviceIDs());
      setDevices(data);
      setError(null);
    } catch (err) {
      setError('加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices, location.key, location.state]);

  const handleDeviceClick = (device) => {
    navigate(`/device/${encodeURIComponent(device.device_id)}/${encodeURIComponent(device.mac)}`);
  };

  const filteredDevices = devices.filter(device => {
    // 过滤掉没有 IP 的设备
    if (!device.ip) return false;
    
    // 搜索过滤
    return device.hostname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.mac?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <>
        <Loading fullscreen text="加载中..." />
        <TabBar />
      </>
    );
  }

  return (
    <div className="page-container game-page">
      <header className="game-header">
        <h1>🎮 游戏管理</h1>
        <p>管理和监控所有游戏设备</p>
      </header>

      <div className="search-box">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          className="search-input"
          placeholder="搜索设备名称、MAC 或厂商..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="stats-row">
        <div className="stat-item">
          <div className="stat-num">{devices.length}</div>
          <div className="stat-label">总设备</div>
        </div>
        <div className="stat-item">
          <div className="stat-num">{filteredDevices.length}</div>
          <div className="stat-label">当前显示</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={loadDevices}>🔄 刷新</button>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="device-list">
        {filteredDevices.length === 0 ? (
          <EmptyState 
            icon="📭"
            title={searchTerm ? '未找到匹配设备' : '暂无设备'}
            action={!searchTerm && (
              <button className="btn btn-primary" onClick={loadDevices}>重新加载</button>
            )}
          />
        ) : (
          filteredDevices.map((device) => (
            <DeviceCard
              key={device.mac}
              device={device}
              onClick={() => handleDeviceClick(device)}
            />
          ))
        )}
      </div>

      <TabBar />
    </div>
  );
};

export default GameManagement;
